// Thin client for the Grails backend (spring-security-rest, JWT bearer tokens).
// Login: POST /api/login {username, password} -> { access_token, ... }
// Authenticated calls: /api/<controller>/<action> with Authorization: Bearer <token>.
//
// NOTE on token refresh: yatra-app's 401 -> refresh-and-retry (with in-flight collapsing)
// depends on yatra's Oauth2Controller (/oauth2/refreshAccessToken + a stored refreshToken and
// clientid header). This app signs in via /api/login | /signin OTP, which hands back only an
// access token — there is no refresh token to redeem — so refresh is intentionally skipped:
// a 401 simply surfaces as ApiError(401) and the auth layer signs the user out.

// Base origin of the Grails backend. EMPTY = same-origin deploy: calls go to relative `/api/...`
// and the web server (Nginx) proxies `/api/` to the backend. Set an absolute URL only for dev.
export const API_BASE =
  (import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8080").replace(/\/$/, "");

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

export interface LoginResult {
  token: string;
  username: string;
  roles: string[];
  displayName: string;
}

/**
 * Exchange a Google Cloud Identity Platform (iskcon-ciam) ID token for our JWT
 * (SigninController.apiAuthGcip). Works for every provider GCIP federates —
 * Google, Facebook, Apple, Microsoft, email link — as long as the email is verified.
 */
export async function exchangeGcipIdToken(idToken: string): Promise<LoginResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/signin/apiAuthGcip`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken }),
    });
  } catch {
    throw new ApiError(`Cannot reach the server at ${API_BASE || "/api"}.`, 0);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.accessToken) {
    throw new ApiError(data.message ?? "Sign-in failed.", res.status);
  }
  return {
    token: data.accessToken,
    username: data.username ?? "",
    roles: data.roles ?? [],
    displayName: data.displayName ?? data.username ?? "",
  };
}

/** Exchange a Google Identity Services ID token for our JWT (SigninController.apiAuthGoogle). */
export async function exchangeGoogleIdToken(idToken: string): Promise<LoginResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/signin/apiAuthGoogle`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken }),
    });
  } catch {
    throw new ApiError(`Cannot reach the server at ${API_BASE || "/api"}.`, 0);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.accessToken) {
    throw new ApiError(data.message ?? "Google sign-in failed.", res.status);
  }
  return {
    token: data.accessToken,
    username: data.username ?? "",
    roles: data.roles ?? [],
    displayName: data.displayName ?? data.username ?? "",
  };
}

export async function login(username: string, password: string): Promise<LoginResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
  } catch {
    throw new ApiError(`Cannot reach the server at ${API_BASE || "/api"}. Is the backend running?`, 0);
  }
  if (res.status === 401) throw new ApiError("Invalid email, mobile number, or password.", 401);
  if (!res.ok) throw new ApiError(`Login failed (HTTP ${res.status}).`, res.status);
  const data = await res.json();
  const token = data.access_token ?? data.token;
  if (!token) throw new ApiError("Login response did not include a token.", res.status);
  return {
    token,
    username: data.username ?? username,
    roles: data.roles ?? [],
    displayName: data.displayName ?? data.username ?? username,
  };
}

export interface MeProfile {
  username: string;
  email: string;
  displayName: string | null;
  displayNameWithTitle: string | null;
  devoteeId: number | null;
  roles: string[];
}

/** Current-user profile. Throws ApiError(401) if the token is invalid/expired. */
export async function fetchMe(token: string): Promise<MeProfile> {
  return apiFetch<MeProfile>("/api/me", { token, method: "POST", body: {} });
}

interface ApiFetchOptions {
  token?: string | null;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export async function apiFetch<T = unknown>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const { token, method = "GET", body, headers, signal } = opts;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(headers ?? {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    if ((e as Error | undefined)?.name === "AbortError") throw e;
    throw new ApiError(`Cannot reach the server at ${API_BASE || "/api"}.`, 0);
  }
  if (res.status === 401) throw new ApiError("Session expired — please sign in again.", 401);
  const text = await res.text();
  if (!res.ok) {
    let detail = "";
    try {
      const errorBody = text ? JSON.parse(text) : null;
      detail = errorBody?.message || errorBody?.errorMessage || errorBody?.error || "";
    } catch {
      detail = text.trim();
    }
    throw new ApiError(
      `${path} failed (HTTP ${res.status})${detail ? `: ${detail}` : ""}.`,
      res.status
    );
  }
  // Some endpoints may return empty bodies.
  return (text ? JSON.parse(text) : null) as T;
}

/**
 * Multipart upload with progress (ported from store-frontend). fetch() cannot report upload
 * progress, so this uses XHR. `onProgress` gets 0–100, or null when the total is unknown.
 */
export function apiUploadFileWithProgress<T = unknown>(
  path: string,
  token: string | null,
  file: File,
  onProgress?: (percent: number | null) => void,
  field = "file"
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const form = new FormData();
    form.append(field, file);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}${path}`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (!onProgress) return;
      onProgress(e.lengthComputable ? Math.round((e.loaded / e.total) * 100) : null);
    };
    xhr.onload = () => {
      if (xhr.status === 401) {
        reject(new ApiError("Session expired — please sign in again.", 401));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new ApiError(`${path} failed (HTTP ${xhr.status})${xhr.responseText ? `: ${xhr.responseText}` : ""}.`, xhr.status));
        return;
      }
      try {
        resolve((xhr.responseText ? JSON.parse(xhr.responseText) : {}) as T);
      } catch {
        reject(new ApiError(`${path} returned an invalid response.`, xhr.status));
      }
    };
    xhr.onerror = () => reject(new ApiError(`Cannot reach the server at ${API_BASE || "/api"}.`, 0));
    xhr.send(form);
  });
}

/**
 * Authenticated binary fetch → Blob (for endpoints that stream a file, e.g.
 * apiDownloadAttachment). The caller turns the blob into an object URL to view it.
 */
export async function apiFetchBlob(path: string, token: string | null): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch {
    throw new ApiError(`Cannot reach the server at ${API_BASE || "/api"}.`, 0);
  }
  if (res.status === 401) throw new ApiError("Session expired — please sign in again.", 401);
  if (!res.ok) throw new ApiError(`${path} failed (HTTP ${res.status}).`, res.status);
  return res.blob();
}

/**
 * Authenticated POST (JSON body) that returns a file — e.g. a server-built .xlsx. Resolves to the
 * blob plus the file name from Content-Disposition (falls back to `fallbackName`).
 */
export async function apiDownload(
  path: string,
  token: string | null,
  body: unknown,
  fallbackName: string
): Promise<{ blob: Blob; fileName: string }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(`Cannot reach the server at ${API_BASE || "/api"}.`, 0);
  }
  if (res.status === 401) throw new ApiError("Session expired — please sign in again.", 401);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let detail = "";
    try {
      detail = JSON.parse(text)?.message ?? "";
    } catch {
      detail = text.trim();
    }
    throw new ApiError(`${path} failed (HTTP ${res.status})${detail ? `: ${detail}` : ""}.`, res.status);
  }
  const cd = res.headers.get("Content-Disposition") ?? "";
  const m = /filename="?([^";]+)"?/i.exec(cd);
  return { blob: await res.blob(), fileName: m?.[1] ?? fallbackName };
}

/** Hands a downloaded blob to the browser as a file to save. */
export function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// --- Email OTP login (permitAll SigninController actions; verify returns a JWT) ---
export async function requestOtp(email: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/signin/apiSendLoginOtp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
  } catch {
    throw new ApiError(`Cannot reach the server at ${API_BASE || "/api"}. Is the backend running?`, 0);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.result === "ERROR") {
    throw new ApiError(data.message ?? `Could not send OTP (HTTP ${res.status}).`, res.status);
  }
}

export async function verifyOtp(email: string, otp: string): Promise<LoginResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/signin/apiVerifyLoginOtp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, otp }),
    });
  } catch {
    throw new ApiError(`Cannot reach the server at ${API_BASE || "/api"}.`, 0);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.accessToken) {
    throw new ApiError(data.message ?? "Invalid or expired OTP.", res.status);
  }
  return {
    token: data.accessToken,
    username: data.username ?? email,
    roles: data.roles ?? [],
    displayName: data.displayName ?? data.username ?? email,
  };
}
